import { marked, Token, Tokens } from 'marked';
import { GDAIChatSource, NovaAvaStreamImage } from '@/types';
import {
  novaStreamingHeadlineClassName,
  novaStreamingSectionHeadingClassName,
  novaStreamingSubsectionHeadingClassName,
} from './novaTheme';

// Turns a Nova answer's markdown into the "report card" structure from the Nova.ai
// spec: a headline (first heading), a subtitle (the paragraph right after it), and
// numbered sections (each subsequent heading + the paragraphs under it). Responses
// that don't happen to use headings (plain prose, which is what the AVA stream
// mostly returns today) fall back to `bodyHtml` — a plain rendered block — so the
// card still shows *something* structured-looking without inventing sections.

export type NovaAnswerImage = { url: string; caption?: string };
/** groupLabel is set when the model wrote a heading of its own immediately before
 * this one with nothing but another heading after it — a group title introducing
 * several sections (e.g. "Key innovation themes" before several numbered findings),
 * not a content section itself. Rendered as an unnumbered label above this section
 * instead of an empty numbered entry of its own. */
export type NovaAnswerSection = { heading: string; html: string; groupLabel?: string; subsection?: boolean };

// `id` is only set when the model wrote its own explicit number for this source
// (the bare "[n] [Title](url)" shape below) — that number is preserved as-is so it
// stays the true citation id even if extraction order doesn't match it exactly.
// Left undefined for label-introduced link lists, where position is all there is.
export type NovaAnswerSource = { title: string; href: string; id?: number };

export type NovaAnswerStructure = {
  preamble?: string;
  headline?: string;
  subtitle?: string;
  sections: NovaAnswerSection[];
  images: NovaAnswerImage[];
  bodyHtml?: string;
  /** Citations the model wrote into its own "Sources"/"References" marker, extracted
   * out so they render through the same "Reference content" component as structured
   * `message.sources` rather than as a plain link list mixed into the answer body. */
  parsedSources: NovaAnswerSource[];
  /** The model's own "Related Questions"/"Follow-up Questions" numbered list,
   * extracted out so it renders as clickable suggestion chips instead of plain
   * numbered text duplicated in the answer body. */
  relatedQuestions: string[];
};

// Both call sites below interpolate a URL straight into an `href="..."` attribute
// of a string that ends up injected via dangerouslySetInnerHTML — a raw `"` in
// that URL (a malformed backend `meta_Link`, or a markdown link whose URL segment
// contains one, since markdown itself only delimits a link's URL by `)`/whitespace,
// not by quotes) would otherwise break out of the attribute and inject markup.
const escapeAttr = (value: string): string =>
  value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const renderer = new marked.Renderer();
renderer.link = ({ href, title, tokens }) => {
  const text = marked.Parser.parseInline(tokens);
  const titleAttr = title ? ` title="${escapeAttr(title)}"` : '';
  return `<a href="${escapeAttr(href)}" target="_blank" rel="noopener noreferrer"${titleAttr}>${text}</a>`;
};

const inlineHtml = (token: Tokens.Heading | Tokens.Paragraph): string =>
  marked.Parser.parseInline(token.tokens, { renderer }) as string;

// Section headings render next to their own "01", "02", ... index (see NovaAnswerCard),
// but the model often writes headings as "1. Heading text" too — strip that leading
// number so it isn't shown twice.
// The marker must be followed by whitespace, so "1. Title" and "2) Title" lose their number but
// a heading that merely starts with one ("1.5 billion shoppers…", "3.2% growth") is left alone.
const stripLeadingNumber = (html: string): string => html.replace(/^\s*\d+[.)]\s+/, '');

// Nova's design spec originally called for "No bracketed [1][2] citations in running
// text, ever" — sourcing was meant to render only through the dedicated "Reference
// content" component. Product has since asked to keep the model's own inline [1][2]
// markers visible on screen after all, so a claim can be traced to its numbered chip;
// this stripped version now exists only for the copy/export path, where a citation
// number with nothing to point to would just be noise.
//
// This runs on already-rendered HTML, so it has to skip over <a>/<code>/<pre> blocks
// wholesale rather than blindly matching [digit] anywhere — otherwise a code sample
// like `items[1]` or a URL containing a bracketed path segment gets corrupted too.
const CITATION_OR_PROTECTED_RE = /<(a|code|pre)\b[^>]*>[\s\S]*?<\/\1>|\[(\d+)\]/gi;
export const stripCitationMarkers = (html: string): string =>
  html.replace(CITATION_OR_PROTECTED_RE, (match) => (match.startsWith('<') ? match : ''));

// Turns a [n] marker into a link to the source it actually names, the same way
// modules/AIchatIntegration.tsx matches `[n]` against `reference.citation_id` rather
// than trusting that n happens to equal the source's position in the rendered chip
// list — those two can diverge once a marker's number and the chip order come from
// different places (a structured `sources` field vs. this parser's own extraction
// of the model's markdown). `sources` here must carry an `id` equal to the number
// the model itself wrote for that source (see mergeSources for the fallback case).
// Rendered as a small raised badge (see citationBadge below) so it reads as a
// citation rather than blending into body text — not inline-sized text at the
// same weight as the sentence it's attached to.
//
// A marker with no matching id (yet) still gets that same badge treatment,
// just without the link — the model writes its "Sources" list at the
// very end of the answer, so while streaming, an earlier section's [n] markers are
// citing sources that haven't arrived yet. Rendering those as plain "[2]" text
// until the source list streams in, then re-rendering as a styled link, is a
// visible style change mid-answer; styling it the same way from the start avoids
// that jump — it just isn't clickable until its source resolves.
//
// The model sometimes cites more than one source for the same claim, writing them
// back-to-back as "[1][2]" with nothing between them — left as-is, two raised
// numbers with no gap read as one number ("12"). This matches that whole run at
// once so each one after the first can be given a small gap when there's more than one.
//
// A citation run isn't always still a bare "[n]" bracket by the time this sees
// it, either: if the model's full answer happens to also include something that
// reads as a markdown link-reference definition for that same number anywhere in
// the document, `marked` resolves the bracket into a real `<a>n</a>` during its
// own parse — invisible while streaming (the definition hasn't arrived yet), but
// flipping the marker over to a plain default-styled link once it has. Matching a
// run of purely-numeric `<a>` tags the same way as bracket runs re-badges those
// too, discarding whatever href `marked` assigned since the source/image lookup
// below is authoritative anyway. `<code>`/`<pre>` blocks are left alone so a code
// sample like `items[1]` isn't corrupted.
// An anchor's text may also still carry its brackets ("<a>[49]</a>", from a model-written
// "[[49]](url)"), so those runs are matched too rather than left as blue "[49]" link text.
const CITATION_RUN_OR_PROTECTED_RE =
  /<(code|pre)\b[^>]*>[\s\S]*?<\/\1>|(?:<a\b[^>]*>\s*\[?\d+\]?\s*<\/a>|\[\d+\])+/gi;

// Every inline [n] marker — whether it names a text source or one of the answer's
// own images (see NovaAvaStreamImage) — renders as the same small filled-circle
// "chip", the same look as the badge already drawn over an image's own thumbnail
// (see NovaAnswerCard/NovaMessage). A single consistent badge reads as "this is a
// numbered citation" wherever it appears (running text, table cells), rather than
// a plain coloured digit for text sources and only a circle for images. Colours
// come from --nova-image-citation-bg/-fg (see novaTableVars) so it still tracks
// the current light/dark theme instead of a hardcoded colour.
const CITATION_BADGE_STYLE =
  'display:inline-flex;align-items:center;justify-content:center;min-width:14px;height:14px;padding:0 3px;' +
  'border-radius:9999px;background:var(--nova-image-citation-bg,#000);color:var(--nova-image-citation-fg,#fff);' +
  'font-size:9px;line-height:14px;font-weight:600;text-decoration:none;vertical-align:middle;';

// A marker with no matching source/image (yet) still gets the same badge treatment,
// just as an unlinked <span> — the model writes its "Sources" list at the very end
// of the answer, so while streaming, an earlier section's [n] markers are citing
// sources that haven't arrived yet. Rendering those as a plain badge from the start,
// then swapping in the link once the source resolves, avoids a visible style change
// mid-answer.
const citationBadge = (num: string, href?: string): string =>
  href
    ? `<a href="${escapeAttr(href)}" target="_blank" rel="noopener noreferrer" style="${CITATION_BADGE_STYLE}">${num}</a>`
    : `<span style="${CITATION_BADGE_STYLE}">${num}</span>`;

// Pulls citation numbers only from a run's actual marker text — either a bracket
// ("[12]") or an anchor's visible text ("<a href=\"...\">12</a>") — never from
// inside an anchor's own attributes, where a source URL's date/signature/id
// segments are just as much a run of digits as a real citation number is.
const CITATION_NUMBER_RE = /<a\b[^>]*>\s*\[?(\d+)\]?\s*<\/a>|\[(\d+)\]/g;

const PROTECTED_TAG_RE = /^<(code|pre)\b/i;

// The model sometimes follows a numbered citation with its own redundant copy of
// that source's raw URL, written as plain parenthetical text right after the
// marker (e.g. "claims.[10] (https://very-long-sharepoint-url...)"). marked's GFM
// autolinking turns the bare URL into a real, fully-rendered <a> whose visible
// text is the URL itself — since the numbered badge just before it already links
// to the same source, this second link adds no information, and being one long
// unbroken string, it's also what forces the answer card wider than intended
// (see novaTableVars/novaRichTextTableClassName's break-words comment). Stripped
// outright — including a literal "(" ")" the model wrote around it, since those
// are plain text sitting outside the <a> tag, not part of it — rather than
// styled, since there's nothing here worth keeping once the badge exists.
const RAW_URL_LINK_OR_PROTECTED_RE = /<(code|pre)\b[^>]*>[\s\S]*?<\/\1>|\(?\s*<a\b[^>]*>\s*https?:\/\/[^\s<]*\s*<\/a>\s*\)?/gi;
const stripRawUrlLinks = (html: string): string =>
  html.replace(RAW_URL_LINK_OR_PROTECTED_RE, (match) => (PROTECTED_TAG_RE.test(match) ? match : ''));

export const findImageSource = (image: NovaAvaStreamImage, sources: GDAIChatSource[]): GDAIChatSource | undefined => {
  const documentUrl = image.document_url?.trim() ?? '';
  if (/^https?:\/\//i.test(documentUrl)) {
    return { id: image.citation_number ?? 0, title: image.title?.trim() ?? '', meta_Link: documentUrl, content: '' };
  }
  const cited = image.citation_number != null ? sources.find((s) => s.id === image.citation_number && s.meta_Link) : undefined;
  if (cited) return cited;
  return /^https?:\/\//i.test(image.source ?? '') && image.source !== image.url
    ? { id: image.citation_number ?? 0, title: '', meta_Link: image.source, content: '' }
    : undefined;
};

export const imageCaption = (image: NovaAvaStreamImage): string => image.image_caption?.trim() ?? '';

export const linkCitationMarkers = (html: string, sources: GDAIChatSource[], images: NovaAvaStreamImage[] = []): string =>
  stripRawUrlLinks(
    html.replace(CITATION_RUN_OR_PROTECTED_RE, (run) => {
      if (PROTECTED_TAG_RE.test(run)) return run;
      const parts = [...run.matchAll(CITATION_NUMBER_RE)].map(([, anchorNum, bracketNum], index) => {
        const num = anchorNum ?? bracketNum;
        const source = sources.find((s) => s.id === Number(num));
        const image = source ? undefined : images.find((img) => img.citation_number === Number(num));
        const badge = citationBadge(num, source?.meta_Link ?? (image && (findImageSource(image, [])?.meta_Link ?? image.url)));
        // Badges after the first in a run get a small gap instead of a separating comma,
        // so two adjacent filled circles still read as two numbers, not one ("23").
        return index > 0 ? `<sup style="margin-left:3px">${badge}</sup>` : `<sup>${badge}</sup>`;
      });
      return parts.join('');
    }),
  );

// marked's default escaping only ever produces these five entities, so a full HTML
// entity decoder isn't needed here — this text is about to be rendered as plain
// React text (not HTML), so `&amp;` etc. must become their literal characters or
// they'd show up verbatim in the UI.
const HTML_ENTITIES: Record<string, string> = { amp: '&', lt: '<', gt: '>', quot: '"', '#39': "'" };
const decodeEntities = (text: string): string => text.replace(/&(amp|lt|gt|quot|#39);/g, (_, entity) => HTML_ENTITIES[entity]);

const toPlainText = (html: string): string => decodeEntities(html.replace(/<[^>]+>/g, '').trim());

const extractLinks = (html: string): NovaAnswerSource[] => {
  const links: NovaAnswerSource[] = [];
  // Quotes of either kind: the model's own raw-HTML source blocks (see
  // extractDetailsSources) don't always use double quotes the way marked does.
  const linkRe = /<a\s+[^>]*href=["']([^"']+)["'][^>]*>([\s\S]*?)<\/a>/g;
  let match: RegExpExecArray | null;
  while ((match = linkRe.exec(html))) {
    links.push({ href: decodeEntities(match[1]), title: toPlainText(match[2]) });
  }
  return links;
};

// A title the model prefixed with its own citation number ("[12] Title" / "12. Title")
// keeps that number as the source's id, same as extractNumberedSourceParagraph does.
const LEADING_CITATION_RE = /^\[?(\d+)[\].)]\s*/;
const withLeadingCitationId = (source: NovaAnswerSource): NovaAnswerSource => {
  const match = source.title.match(LEADING_CITATION_RE);
  return match ? { ...source, id: Number(match[1]), title: source.title.slice(match[0].length) } : source;
};

// AVA's current <details> source list puts the title *outside* the link, with only
// the citation number as link text — `- LFW S/S 27: Footwear [\[1\]](url)`. Taking
// the link text as the title (what extractLinks does) leaves just "[1]", which
// withLeadingCitationId then strips to an empty title, so the chip showed only its
// number. For a list item whose sole link is a bare citation marker, the item's
// remaining text is the title instead. Anything else keeps the link-text behaviour.
const BARE_CITATION_RE = /^\[?(\d+)\]?$/;
const LIST_ITEM_RE = /<li\b[^>]*>([\s\S]*?)<\/li>/gi;
const ANCHOR_RE = /<a\b[^>]*>[\s\S]*?<\/a>/gi;
const TITLE_EDGE_PUNCTUATION_RE = /^[\s\-–—:|]+|[\s\-–—:|]+$/g;

const extractChunkLinks = (chunk: string): NovaAnswerSource[] => {
  const links = extractLinks(chunk);
  const citation = links.length === 1 ? links[0].title.match(BARE_CITATION_RE) : null;
  if (citation) {
    const title = toPlainText(chunk.replace(ANCHOR_RE, '')).replace(TITLE_EDGE_PUNCTUATION_RE, '');
    if (title) return [{ href: links[0].href, id: Number(citation[1]), title }];
  }
  return links.map(withLeadingCitationId);
};

// Each <li> is its own chunk; whatever's left outside the list items (a blank line
// inside the block can split some sources into a separate paragraph) is one more
// chunk, so those links are still picked up rather than dropped.
const extractSourceListLinks = (html: string): NovaAnswerSource[] => {
  const items = [...html.matchAll(LIST_ITEM_RE)].map((match) => match[1]);
  const outsideItems = html.replace(LIST_ITEM_RE, '');
  return [...items, outsideItems].flatMap(extractChunkLinks);
};

// AVA sometimes writes its sourcing as a raw HTML collapsible —
// `<details><summary>View Sources</summary><ul><li><a href=...>Title</a></li>...</ul></details>` —
// which marked passes straight through as an `html` token, so it rendered as a
// browser-default disclosure widget with a plain blue link list instead of the
// numbered "Reference content" chips. A blank line inside the block splits it
// across several tokens (and may turn the list into a markdown list token), so
// this spans from the token holding the "Sources" <summary> (or the <details>
// right before it) to the one closing </details>.
//
// Only real `html` block tokens are considered, so a code sample that happens to
// contain this markup is never mistaken for it. With no closing </details> (still
// streaming, or the model never closed it) the span only extends over tokens a source
// list can actually consist of — stopping at the first prose/heading/table — so
// the answer text after an unclosed block is never swallowed along with it.
const DETAILS_SOURCES_SUMMARY_RE = /<summary\b[^>]*>[^<]*\b(sources?|references?)\b[^<]*<\/summary>/i;
const isHtmlToken = (token: Token | undefined, re: RegExp): boolean => token?.type === 'html' && re.test(token.raw);
const SOURCE_LIST_TOKEN_TYPES = new Set(['html', 'list', 'space']);

const extractDetailsSources = (tokens: Token[]): { tokens: Token[]; parsedSources: NovaAnswerSource[] } | null => {
  const summaryIndex = tokens.findIndex((token) => isHtmlToken(token, DETAILS_SOURCES_SUMMARY_RE));
  if (summaryIndex === -1) return null;

  const start =
    !/<details\b/i.test(tokens[summaryIndex].raw) && isHtmlToken(tokens[summaryIndex - 1], /<details\b/i)
      ? summaryIndex - 1
      : summaryIndex;

  let end = tokens.findIndex((token, i) => i >= summaryIndex && isHtmlToken(token, /<\/details>/i));
  if (end === -1) {
    end = summaryIndex;
    while (end + 1 < tokens.length && SOURCE_LIST_TOKEN_TYPES.has(tokens[end + 1].type)) end++;
  }

  const blockHtml = (marked.parser(tokens.slice(start, end + 1), { renderer }) as string).replace(
    DETAILS_SOURCES_SUMMARY_RE,
    '',
  );
  // AVA lists these in arbitrary order (e.g. 1, 3, 2) — show the chips numerically.
  // Stable sort, and sources without an id stay where they were relative to each other.
  const parsedSources = extractSourceListLinks(blockHtml).sort(
    (a, b) => (a.id ?? Number.MAX_SAFE_INTEGER) - (b.id ?? Number.MAX_SAFE_INTEGER),
  );
  // A block with no links in it isn't the source list after all — leave the
  // tokens alone so the other source-detection cases still get their turn.
  if (parsedSources.length === 0) return null;
  return {
    tokens: [...tokens.slice(0, start), ...tokens.slice(end + 1)],
    parsedSources,
  };
};

// The AVA stream marks its own sourcing section a few different ways depending on
// the response: an actual "## Sources" heading, a bare label line like "Sources:"
// (plain or bolded) as its own paragraph, or — what it actually writes most often,
// since there's rarely a blank line between the label and its links — the label as
// just the first line of one paragraph that also contains every link. All three
// need finding and removing the same way, so both parseNovaAnswer and the lighter
// extractInlineSources share this instead of only ever checking for a heading.
const SOURCES_LABEL_RE = /^\*{0,2}(sources?|references?)\*{0,2}:?$/i;
// A stop boundary for the standalone-label case: another short bare label line (e.g.
// "Related Questions:") that follows the source list and should stay untouched.
const NEXT_LABEL_RE = /^\*{0,2}[^*\n:]{1,40}\*{0,2}:$/;

const firstLine = (text: string): string => text.split('\n')[0].trim();

// AVA's most common shape today: a paragraph of bare "[n] [Title](url)" lines with
// *no* "Sources:"/"References:" label at all — nothing for the label-based cases
// below (all three key off finding that label first) to match. Detected by shape
// instead: a paragraph where every line is exactly that pattern. The model's own
// number is kept as each source's `id` (see NovaAnswerSource) since it's the exact
// citation number used elsewhere in the text — no need to assume position order.
const NUMBERED_SOURCE_LINE_RE = /^\[(\d+)\]\s*\[([^\]]*)\]\(([^)]+)\)$/;

// Shared with extractRelatedQuestions below: AVA sometimes runs its "Related
// Questions:" label straight on from the numbered source list with no blank line,
// which marked folds into the *same* paragraph token as the source lines — that
// last line has to be recognised and split back out here so it still becomes its
// own paragraph for extractRelatedQuestions to find.
const RELATED_QUESTIONS_LABEL_RE = /^\*{0,2}(related questions|follow-?up questions|suggested questions)\*{0,2}:?$/i;

const extractNumberedSourceParagraph = (tokens: Token[]): { tokens: Token[]; parsedSources: NovaAnswerSource[] } | null => {
  const index = tokens.findIndex((token) => {
    if (token.type !== 'paragraph') return false;
    const lines = (token as Tokens.Paragraph).raw
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);
    if (lines.length === 0) return false;
    const sourceLines = RELATED_QUESTIONS_LABEL_RE.test(lines[lines.length - 1]) ? lines.slice(0, -1) : lines;
    return sourceLines.length > 0 && sourceLines.every((line) => NUMBERED_SOURCE_LINE_RE.test(line));
  });
  if (index === -1) return null;

  const lines = (tokens[index] as Tokens.Paragraph).raw
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
  const trailingLabel = RELATED_QUESTIONS_LABEL_RE.test(lines[lines.length - 1]);
  const sourceLines = trailingLabel ? lines.slice(0, -1) : lines;

  const parsedSources = sourceLines.map((line) => {
    const [, id, title, href] = line.match(NUMBERED_SOURCE_LINE_RE)!;
    return { id: Number(id), title, href };
  });

  if (!trailingLabel) {
    return { tokens: [...tokens.slice(0, index), ...tokens.slice(index + 1)], parsedSources };
  }

  // The label line still needs to exist as its own paragraph token, immediately
  // before the question list (which already tokenizes separately — see
  // extractRelatedQuestions), for that function to still find it.
  const labelToken = marked.lexer(lines[lines.length - 1])[0];
  return { tokens: [...tokens.slice(0, index), labelToken, ...tokens.slice(index + 1)], parsedSources };
};

// Splits a token stream around its sourcing marker (if any): the remaining tokens
// with that marker (and whatever links it introduced) removed, plus those links
// extracted as a fallback source list.
const extractSources = (tokens: Token[]): { tokens: Token[]; parsedSources: NovaAnswerSource[] } => {
  // Case 0: a raw-HTML <details><summary>View Sources</summary> block.
  const detailsSources = extractDetailsSources(tokens);
  if (detailsSources) return detailsSources;

  // Case 1: an actual "## Sources" heading — its section (up to the next heading,
  // or another bare label line like "Related Questions:" that follows it and
  // should stay untouched) is the source list, however it's formatted (list
  // items, or bare link paragraphs).
  const headingIndex = tokens.findIndex(
    (token) => token.type === 'heading' && SOURCES_LABEL_RE.test(toPlainText(inlineHtml(token as Tokens.Heading))),
  );
  if (headingIndex !== -1) {
    let end = tokens.length;
    for (let i = headingIndex + 1; i < tokens.length; i++) {
      const token = tokens[i];
      if (token.type === 'heading' || (token.type === 'paragraph' && NEXT_LABEL_RE.test(firstLine(token.text)))) {
        end = i;
        break;
      }
    }
    const sourcesTokens = tokens.slice(headingIndex + 1, end);
    return {
      tokens: [...tokens.slice(0, headingIndex), ...tokens.slice(end)],
      parsedSources: extractLinks(marked.parser(sourcesTokens, { renderer }) as string),
    };
  }

  // Case 2: a paragraph whose first line is the bare label — with no blank line
  // separating it from its links, marked folds the label and every link into one
  // paragraph token, so the links have to be pulled out of the rest of *this same*
  // paragraph's raw text rather than from separate tokens after it.
  const paragraphIndex = tokens.findIndex(
    (token) => token.type === 'paragraph' && SOURCES_LABEL_RE.test(firstLine((token as Tokens.Paragraph).text)),
  );
  // No "Sources:"/"References:" label anywhere — fall back to shape-based
  // detection (extractNumberedSourceParagraph) rather than giving up, since AVA's
  // most common shape today has no label at all. Tried last, after the
  // label-based cases, so a real "## Sources" heading (Case 1) always wins over a
  // shape match against what would otherwise be that heading's own body.
  if (paragraphIndex === -1) return extractNumberedSourceParagraph(tokens) ?? { tokens, parsedSources: [] };

  const paragraphToken = tokens[paragraphIndex] as Tokens.Paragraph;
  const linksMarkdown = paragraphToken.raw.split('\n').slice(1).join('\n').trim();
  if (linksMarkdown) {
    return {
      tokens: [...tokens.slice(0, paragraphIndex), ...tokens.slice(paragraphIndex + 1)],
      parsedSources: extractLinks(marked.parse(linksMarkdown, { renderer }) as string),
    };
  }

  // Case 3: a standalone label paragraph with nothing else on it — whatever
  // list/paragraphs follow (up to the next heading or another bare label, e.g.
  // "Related Questions:") are the source list instead.
  let end = tokens.length;
  for (let i = paragraphIndex + 1; i < tokens.length; i++) {
    const token = tokens[i];
    if (token.type === 'heading' || (token.type === 'paragraph' && NEXT_LABEL_RE.test(firstLine(token.text)))) {
      end = i;
      break;
    }
  }
  const sourcesTokens = tokens.slice(paragraphIndex + 1, end);
  return {
    tokens: [...tokens.slice(0, paragraphIndex), ...tokens.slice(end)],
    parsedSources: extractLinks(marked.parser(sourcesTokens, { renderer }) as string),
  };
};

// AVA doesn't send a separate `suggestedQuestions` field on the stream (confirmed
// against a captured response — the only chunk shapes actually seen are
// {"type":"response","message":...} and a trailing {"type":"context_status",...}).
// Instead the model writes a "Related Questions:" label of its own, followed
// immediately by a genuine ordered-list token — CommonMark lets a list starting at
// "1." interrupt a paragraph with no blank line needed, so marked reliably splits
// them into two adjacent tokens rather than folding the list into the label's
// paragraph the way the label-plus-links sourcing case above can — except when the
// label itself runs on directly from a numbered source list with no blank line
// (see extractNumberedSourceParagraph, which splits that case back out for this
// function to still find).
const extractRelatedQuestions = (tokens: Token[]): { tokens: Token[]; relatedQuestions: string[] } => {
  const labelIndex = tokens.findIndex((token) => {
    if (token.type === 'heading') return RELATED_QUESTIONS_LABEL_RE.test(toPlainText(inlineHtml(token as Tokens.Heading)));
    if (token.type === 'paragraph') return RELATED_QUESTIONS_LABEL_RE.test(firstLine((token as Tokens.Paragraph).text));
    return false;
  });
  if (labelIndex === -1) return { tokens, relatedQuestions: [] };

  const listToken = tokens[labelIndex + 1];
  if (listToken?.type !== 'list') return { tokens, relatedQuestions: [] };

  const relatedQuestions = (listToken as Tokens.List).items.map((item) => item.text);

  return { tokens: [...tokens.slice(0, labelIndex), ...tokens.slice(labelIndex + 2)], relatedQuestions };
};

// While an answer streams it renders as one flowing block (see NovaAnswerCard), so its
// headings get the site's generic `.richText` sizes and keep the "1." the model writes in
// front of a section title — then the finished card re-lays them out and they visibly resize
// and lose their numbers. This gives every heading its final treatment up front, by running
// the same classification parseNovaAnswer does: the first heading is the headline; each later
// one is a section title, except a heading with nothing under it but another heading (a group
// label, styled like a section), and sections nested under a shallower group label, which get
// the smaller subsection size. Leading numbers are stripped from every heading, as the card
// does. The last heading is assumed to have a body still on its way. Classes (not
// inline styles) so the sizes can change at the card's breakpoint, as the final ones do.
const HEADING_ELEMENT_RE = /<(h[1-6])(\s[^>]*)?>([\s\S]*?)<\/\1>/gi;

export const styleStreamingHeadings = (html: string): string => {
  const headings = [...html.matchAll(HEADING_ELEMENT_RE)];
  if (headings.length === 0) return html;

  const hasBodyAfter = (i: number): boolean => {
    if (i === headings.length - 1) return true;
    const from = (headings[i].index ?? 0) + headings[i][0].length;
    return html.slice(from, headings[i + 1].index).trim() !== '';
  };

  const classNames: string[] = [novaStreamingHeadlineClassName];
  let groupDepth: number | undefined;
  let pendingGroupDepth = Infinity;
  let pendingGroup = false;
  for (let i = 1; i < headings.length; i++) {
    const depth = Number(headings[i][1][1]);
    if (!hasBodyAfter(i)) {
      pendingGroup = true;
      pendingGroupDepth = Math.min(pendingGroupDepth, depth);
      classNames.push(novaStreamingSectionHeadingClassName);
      continue;
    }
    if (pendingGroup) groupDepth = pendingGroupDepth < depth ? pendingGroupDepth : undefined;
    else if (groupDepth !== undefined && depth <= groupDepth) groupDepth = undefined;
    classNames.push(groupDepth !== undefined ? novaStreamingSubsectionHeadingClassName : novaStreamingSectionHeadingClassName);
    pendingGroup = false;
    pendingGroupDepth = Infinity;
  }

  let index = 0;
  return html.replace(HEADING_ELEMENT_RE, (_match, tag: string, attrs: string | undefined, inner: string) => {
    const position = index++;
    const content = stripLeadingNumber(inner);
    return `<${tag}${attrs ?? ''} class="${classNames[position]}">${content}</${tag}>`;
  });
};

// One entry per article for the reference chips and the export list: the citations
// event can list the same article several times (a text chunk plus figures from it),
// each with its own number so inline [n] badges resolve exactly, but listing "Colour
// Launches for 2026" three times as chips would just be noise. The first (lowest-
// numbered) entry for a URL is kept; sources with no URL are kept as they are.
export const dedupeSourcesByLink = (sources: GDAIChatSource[]): GDAIChatSource[] => {
  const seen = new Set<string>();
  return sources.filter((source) => {
    const key = source.meta_Link?.trim();
    if (!key) return true;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

// What copy and export carry. The answer's own "Sources" and "Related Questions" blocks
// are left out — on screen they're the reference chips and follow-up chips — and the
// [n] markers are stripped. In place of the model's sourcing text, a "References" list
// is built from the same merged sources the chips use, each as a real link, so a copied
// or downloaded answer points at the actual pages instead of carrying static text.
// A source with no URL is listed by title only.
const escapeHtml = (value: string): string => value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

export const buildNovaExportHtml = (
  markdown: string,
  sources: GDAIChatSource[],
  images: NovaAvaStreamImage[] = [],
): string => {
  const text = stripCitationMarkers(extractInlineSources(markdown).html);

  const imageTags = images
    .filter((image) => image.url && !text.includes(image.url) && !text.includes(escapeAttr(image.url)))
    .map((image) => `<p><img src="${escapeAttr(image.url)}" alt=""></p>`);
  const body = imageTags.length > 0 ? `${text}<h2>Images</h2>${imageTags.join('')}` : text;

  const items = dedupeSourcesByLink(sources)
    .map((source) => {
      const title = escapeHtml((source.title || source.meta_Link || '').trim());
      if (!title) return '';
      return source.meta_Link ? `<li><a href="${escapeAttr(source.meta_Link)}">${title}</a></li>` : `<li>${title}</li>`;
    })
    .filter(Boolean);

  return items.length > 0 ? `${body}<h2>References</h2><ol>${items.join('')}</ol>` : body;
};

// Plain markdown-to-html render, sharing the same link renderer as the structured
// parse below. Used while an answer is still streaming in, so the view doesn't have
// to wait for (or re-derive) the headline/sections structure token-by-token.
export const renderNovaMarkdown = (markdown: string): string => marked.parse(markdown || '', { renderer }) as string;

// Lighter-weight version of parseNovaAnswer for surfaces (the floating widget) that
// render the whole answer as one flowing block rather than a headline/sections report
// card — still strips the model's own sourcing marker out of the flow (extracted as
// a fallback source list instead), but keeps its inline [1][2] markers: despite the
// "no bracketed citations" design spec above, product wants them visible so a claim
// can be traced to its "Reference content" chip. Only the copy/export path (see
// stripCitationMarkers call sites) still removes them.
// The model sometimes writes a link with no visible text — "[\n](https://…s3…png)" —
// which marked can't parse as a link (the newline breaks it), so it rendered as a
// stray "[" plus a raw autolinked S3 URL. Also covers one still streaming in (no
// closing ")" yet). Nothing to show for these, so they're dropped before parsing.
// The preceding character is captured and put back (rather than a `(?<!!)` lookbehind,
// which Safari < 16.4 can't parse — a syntax error there fails this whole module) so
// an image ("![](...)") is left alone.
const EMPTY_LINK_RE = /(^|[^!])\[\s*\]\(\s*https?:\/\/[^\s)]*\)?/g;
const stripEmptyLinks = (markdown: string): string => markdown.replace(EMPTY_LINK_RE, '$1');

export const extractInlineSources = (
  markdown: string,
): { html: string; parsedSources: NovaAnswerSource[]; relatedQuestions: string[] } => {
  const { tokens: tokensAfterSources, parsedSources } = extractSources(marked.lexer(stripEmptyLinks(markdown || '')));
  const { tokens, relatedQuestions } = extractRelatedQuestions(tokensAfterSources);
  return {
    html: marked.parser(tokens, { renderer }) as string,
    parsedSources,
    relatedQuestions,
  };
};

export const parseNovaAnswer = (markdown: string): NovaAnswerStructure => {
  const { tokens: tokensAfterSources, parsedSources } = extractSources(marked.lexer(stripEmptyLinks(markdown || '')));
  const { tokens, relatedQuestions } = extractRelatedQuestions(tokensAfterSources);

  const images: NovaAnswerImage[] = [];
  marked.walkTokens(tokens, (token: Token) => {
    if (token.type === 'image') images.push({ url: token.href, caption: token.text || undefined });
  });

  const headingIndexes = tokens.reduce<number[]>((acc, token, index) => {
    if (token.type === 'heading') acc.push(index);
    return acc;
  }, []);

  if (headingIndexes.length === 0) {
    const html = tokens.length ? (marked.parser(tokens, { renderer }) as string) : undefined;
    return {
      sections: [],
      images,
      bodyHtml: html,
      parsedSources,
      relatedQuestions,
    };
  }

  const [firstHeadingIndex, ...sectionHeadingIndexes] = headingIndexes;
  // Number stripped like the section headings' — "1. Title" reads as a list item, not a title —
  // and so the headline looks the same here as it did while streaming (styleStreamingHeadings).
  const headline = stripLeadingNumber(inlineHtml(tokens[firstHeadingIndex] as Tokens.Heading));

  // Anything generated before the first heading — otherwise it's silently dropped:
  // this function only ever looks at tokens[firstHeadingIndex] onward.
  const preHeadingTokens = tokens.slice(0, firstHeadingIndex);
  const preamble = preHeadingTokens.length > 0 ? (marked.parser(preHeadingTokens, { renderer }) as string) : undefined;

  const leadEnd = sectionHeadingIndexes[0] ?? tokens.length;
  const leadTokens = tokens.slice(firstHeadingIndex + 1, leadEnd);
  const subtitleIndex = leadTokens.findIndex((t) => t.type === 'paragraph');
  const subtitle = subtitleIndex >= 0 ? inlineHtml(leadTokens[subtitleIndex] as Tokens.Paragraph) : undefined;
  const leftoverLeadTokens = subtitleIndex >= 0 ? leadTokens.filter((_, i) => i !== subtitleIndex) : leadTokens;

  const rawSections = sectionHeadingIndexes.map((headingIndex, i) => {
    const nextHeadingIndex = sectionHeadingIndexes[i + 1] ?? tokens.length;
    const bodyTokens = tokens.slice(headingIndex + 1, nextHeadingIndex);
    return {
      heading: stripLeadingNumber(inlineHtml(tokens[headingIndex] as Tokens.Heading)),
      html: marked.parser(bodyTokens, { renderer }) as string,
      depth: (tokens[headingIndex] as Tokens.Heading).depth,
    };
  });
  let groupDepth: number | undefined;
  let pendingGroupDepth = Infinity;

  // A heading with nothing but another heading right after it isn't a content
  // section of its own — it's a group title the model wrote to introduce the
  // section(s) that follow (e.g. "Key innovation themes" before several numbered
  // findings). Fold it into the next real section's groupLabel instead of
  // rendering it as its own empty numbered entry.
  const sections: NovaAnswerSection[] = [];
  let pendingGroupLabels: string[] = [];
  for (const { depth, ...raw } of rawSections) {
    if (!raw.html.trim()) {
      pendingGroupLabels.push(raw.heading);
      pendingGroupDepth = Math.min(pendingGroupDepth, depth);
      continue;
    }
    const groupLabel = pendingGroupLabels.length ? pendingGroupLabels.join(' ') : undefined;
    if (groupLabel) groupDepth = pendingGroupDepth < depth ? pendingGroupDepth : undefined;
    else if (groupDepth !== undefined && depth <= groupDepth) groupDepth = undefined;
    sections.push({ ...raw, groupLabel, subsection: groupDepth !== undefined });
    pendingGroupLabels = [];
    pendingGroupDepth = Infinity;
  }
  // A trailing empty heading has no section after it to fold into — rather than
  // silently discarding it, render it as its own (bodyless) section so the
  // model's content never just vanishes from a completed answer.
  if (pendingGroupLabels.length) {
    sections.push({ heading: pendingGroupLabels.join(' '), html: '' });
  }

  // Any lead paragraphs beyond the single one used as `subtitle` — independent of
  // whether section headings follow, otherwise they're silently dropped (visible
  // as an answer that looks truncated to just headline + one paragraph).
  const bodyHtml = leftoverLeadTokens.length > 0 ? (marked.parser(leftoverLeadTokens, { renderer }) as string) : undefined;

  return {
    preamble,
    headline,
    subtitle,
    sections,
    images,
    bodyHtml,
    parsedSources,
    relatedQuestions,
  };
};

// Structured `message.sources` metadata wins when the backend sends it; otherwise
// falls back to citations the model wrote into its own sourcing marker (parsedSources
// above), so sourcing never silently disappears. Shared by NovaAnswerCard and
// NovaMessage rather than each re-deriving the same fallback independently.
//
// `id` doubles as the citation number linkCitationMarkers matches a [n] marker
// against. The numbered-bracket source shape (extractNumberedSourceParagraph)
// already carries the model's own explicit number, which is kept as-is; the
// label-introduced link-list shapes don't have one, so position in that list
// (1-based) is used instead — the model's own "Sources" list is written in the
// same order it introduced [1], [2], ... inline.
export const mergeSources = (structured: GDAIChatSource[] | undefined, parsed: NovaAnswerSource[]): GDAIChatSource[] => {
  if (structured?.length) return structured;
  return parsed.map((source, index) => ({ id: source.id ?? index + 1, title: source.title, meta_Link: source.href, content: '' }));
};

// Same idea as mergeSources: structured suggestedQuestions (should AVA ever start
// sending them — see extractRelatedQuestions) win when present, otherwise fall
// back to the model's own "Related Questions" list parsed out of its answer text.
export const mergeSuggestedQuestions = (structured: string[] | undefined, parsed: string[]): string[] =>
  structured?.length ? structured : parsed;
